<template>
    <div class="press-release-item pri">
        <div class="pri__row">
            <template v-if="currentReleases && currentReleases.releases">
                <UILink
                    :to="currentReleases.linkTo"
                    :label="`Все ${currentReleases.linkLabel}`"
                    link-class="link-to-page"
                />
                <UISlider
                    v-if="currentReleases.releases && currentReleases.releases.length"
                    id="press-release-slider"
                    :options="swiperOptions"
                    :slides="currentReleases.releases"
                >
                    <template #default="{slide}">
                        <n-link
                            v-if="type !== 'articles'"
                            :class="['pri-item__link', {'disable': !currentReleases.routeName}]"
                            :to="{ name: currentReleases.routeName, params: { slug: slide.alias } }"
                        >
                            <time
                                v-if="['news', 'events'].includes(type)"
                                class="pri-item__date"
                            >
                                {{ dateFormat((slide.newsDate || slide.startDate), {format: 'DD'}) }}
                                <sup class="pri-item__sup">
                                    /{{ dateFormat((slide.newsDate || slide.startDate), {format: 'MM'}) }}
                                </sup>
                            </time>
                            <time
                                v-if="type === 'mass-media'"
                                class="pri-item__date"
                            >
                                {{
                                    dateFormat(slide.activeFrom, {format: 'DD', dateFormat: 'DD.MM.YYYY'})
                                }}
                                <sup class="pri-item__sup">
                                    /{{
                                        dateFormat(slide.activeFrom, {format: 'MM', dateFormat: 'DD.MM.YYYY'})
                                    }}
                                </sup>
                            </time>
                            <h4 class="h5 mb-24 pri-item__title">
                                {{ slide.title }}
                            </h4>
                        </n-link>
                        <a
                            v-else
                            :href="`https://site-api.centrinvest.ru${slide.document}`"
                            target="_blank"
                            class="pri-item__link"
                            rel="noopener noreferrer"
                        >
                            <h4
                                class="h4 mb-16 fz-18 pri-item__article"
                            >
                                {{ slide.title }}
                            </h4>
                            <p
                                class="pri-item__text text-light"
                                v-html="stripTags(getTextItem(slide))"
                            />
                        </a>
                    </template>
                </UISlider>
            </template>
        </div>
    </div>
</template>

<script>
import {defineComponent, computed} from 'vue';
import {useFetch} from '@nuxtjs/composition-api';
import {UILink, UISlider} from '@cibsite/ui-components-vue2';

import {useArticlesStore} from '@/stores/articles';
import {useEventsStore} from '@/stores/events';
import {usePressReleasesStore} from '@/stores/press-releases';
import {useMassMediaStore} from '@/stores/mass-media';
import dateFormat from '@/core/utils/dateFormat';
import stripTags from '@/core/utils/stripTags';

const SLIDER_OPTIONS = {
    slideCustomClasses: 'pri__col pri-item',
    speed: 500,
    autoHeight: true,
    breakpoints: {
        0: {
            slidesPerView: 'auto',
            spaceBetween: 16,
        },
        800: {
            slidesPerView: 2,
        },
        1200: {
            slidesPerView: 3,
        },
    },
};

// TODO какая-то проблемма(предупреждение о записи) с computed. Проверить после перехода.

export default defineComponent({
    name: 'PressReleaseItem',
    components: {
        UILink,
        UISlider,
    },
    props: {
        type: {
            type: String,
            default: () => 'news',
            validator: (value) => {
                return ['news', 'articles', 'events', 'mass-media'].includes(value);
            },
        },
    },
    setup(props) {
        const articlesStore = useArticlesStore();
        const eventsStore = useEventsStore();
        const pressReleasesStore = usePressReleasesStore();
        const massMediaStore = useMassMediaStore();

        const currentReleases = computed(() => {
            switch (props.type) {
                case 'news':
                    return {
                        releases: pressReleasesStore.latestNews,
                        linkLabel: 'новости',
                        linkTo: '/about/press-releases',
                        routeName: 'about-press-releases-slug',
                    };
                case 'articles':
                    return {
                        releases: articlesStore.latestArticles,
                        linkLabel: 'книги и статьи',
                        linkTo: '/about/books-articles',
                        routeName: '',
                    };
                case 'events':
                    return {
                        releases: eventsStore.latestEvents,
                        linkLabel: 'события',
                        linkTo: '/about/events',
                        routeName: '',
                    };
                case 'mass-media':
                    return {
                        releases: massMediaStore.latestMassMedia,
                        linkLabel: 'записи в сми о нас',
                        linkTo: '/about/mass-media',
                        routeName: 'about-mass-media-slug',
                    };
                default:
                    return null;
            }
        });

        function getTextItem(release) {
            const numDot = release.introText.indexOf('.');

            return ['news', 'events'].includes(props.type)
                ? release.title
                : `${release.introText.substr(0, numDot + 1)}`;
        }

        useFetch(async() => {
            switch (props.type) {
                case 'news':
                    await pressReleasesStore.getNewsLatest();
                    break;
                case 'articles':
                    await articlesStore.getArticlesLatest();
                    break;
                case 'events':
                    await eventsStore.getEventsLatest();
                    break;
                case 'mass-media':
                    await massMediaStore.getMassMediaLatest();
                    break;
            }
        });

        return {
            swiperOptions: SLIDER_OPTIONS,
            currentReleases,
            dateFormat,
            stripTags,
            getTextItem,
        };
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/components/home-page/press-release-item';
</style>
